import { useCallback } from "react";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import {
  Button,
  UNSTABLE_Toast as RACToast,
  UNSTABLE_ToastContent as RACToastContent,
  Text,
  UNSTABLE_ToastQueue as ToastQueue,
  UNSTABLE_ToastRegion as ToastRegion
} from "react-aria-components";

import { cn } from "tailwind-variants";

export type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export type ToastProps = {
  title: string;
  description: string;
  variant?: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
    altText: string;
  };
};

type ToastContent = ToastProps;

const variantIcons = {
  default: Info,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info
};

const variantIconColors = {
  default: "text-blue-400",
  success: "text-green-400",
  error: "text-red-400",
  warning: "text-yellow-400",
  info: "text-blue-400"
};

// Global toast queue — module-private singleton
const toastQueue = new ToastQueue<ToastContent>({
  maxVisibleToasts: 5
});

/** Reset the queue between tests. Only exposes clear(), not the full queue API. */
export function clearToastQueue(): void {
  toastQueue.clear();
}

export type ToastProviderProps = {
  children: React.ReactNode;
};

export function ToastProvider({ children }: ToastProviderProps) {
  return (
    <>
      {children}
      <ToastRegion
        queue={toastQueue}
        className="fixed bottom-[300px] right-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 sm:bottom-0 sm:max-w-[420px]"
      >
        {({ toast }) => <ToastItem toast={toast} />}
      </ToastRegion>
    </>
  );
}

type ToastItemProps = {
  toast: { key: string; content: ToastContent };
};

function ToastItem({ toast }: ToastItemProps) {
  const { title, description, variant = "default", action } = toast.content;
  const Icon = variantIcons[variant];
  const iconColor = variantIconColors[variant];

  return (
    <RACToast
      toast={toast}
      data-testid="toast-root"
      className={
        cn(
          "pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border bg-slate-800 p-4 pr-8 text-white shadow-lg",
          "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:slide-in-from-bottom-2",
          "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:slide-out-to-right-2"
        )!
      }
    >
      <RACToastContent>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <Icon className={cn("h-5 w-5", iconColor)} />
              <Text slot="title" className="font-medium text-white">
                {title}
              </Text>
            </div>
            <Text slot="description" className="text-sm text-gray-300">
              {description}
            </Text>
          </div>
        </div>
      </RACToastContent>

      {action && (
        <Button
          data-testid="toast-action-button"
          className="shrink-0 rounded-md border border-gray-600 bg-transparent px-3 py-1.5 text-sm text-white hover:bg-slate-700"
          onPress={action.onClick}
          aria-label={action.altText}
        >
          {action.label}
        </Button>
      )}

      <Button
        slot="close"
        data-testid="toast-close-button"
        className="absolute right-2 top-2 rounded-full p-1 text-white hover:bg-slate-700"
        aria-label="Close toast"
      >
        <X className="h-4 w-4" />
      </Button>
    </RACToast>
  );
}

export function useToast() {
  const toast = useCallback(
    (props: ToastProps) => toastQueue.add(props, { timeout: props.duration ?? 5000 }),
    []
  );

  const dismiss = useCallback((id?: string) => {
    if (id) toastQueue.close(id);
  }, []);

  return {
    toast,
    success: useCallback(
      (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "success" }),
      [toast]
    ),
    error: useCallback(
      (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "error" }),
      [toast]
    ),
    warning: useCallback(
      (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "warning" }),
      [toast]
    ),
    info: useCallback(
      (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "info" }),
      [toast]
    ),
    dismiss
  };
}
